import { useState, useMemo, useEffect } from 'react';
import { isOnboardingDisabled } from 'common/env';
import { OnboardingFeatureHighlights } from 'components/onboarding-feature-highlights/onboarding-feature-highlights.component';
import { OnboardingAccountSetup } from 'components/onboarding-account-setup/onboarding-account-setup.component';
import { useOnboardingCompletion } from '../use-onboarding-completion/use-onboarding-completion.hook';

export const useOnboarding = () => {
  const {
    accountSetupComplete,
    featureHighlightsComplete,
    isLoading: onboardingIsLoading,
    error,
    markFeatureHighlightsComplete,
  } = useOnboardingCompletion();

  // Track whether we've initialized the steps based on onboardingComplete
  const [isInitialized, setIsInitialized] = useState(false);

  // Assume that the steps are completed by default
  const [stepsCompleted, setStepsCompleted] = useState({
    featureHighlights: true,
    accountSetup: true,
  });

  useEffect(() => {
    // Wait until the initial profile load resolves before seeding state.
    if (onboardingIsLoading) return;

    if (isOnboardingDisabled()) {
      setStepsCompleted({
        featureHighlights: true,
        accountSetup: true,
      });
      setIsInitialized(true);
      return;
    }

    if (!isInitialized) {
      // First pass: seed steps from the server-derived flags.
      setStepsCompleted({
        featureHighlights: featureHighlightsComplete,
        accountSetup: accountSetupComplete,
      });
      setIsInitialized(true);
      return;
    }

    // Post-init, only OR-in feature-highlights completion (a stale-cache
    // refetch could otherwise regress it and bounce the user back). Account
    // setup is finalized locally via onOnboardingComplete, not from the server:
    // its flag (e.g. myhealth_consent) is written on the review step, so OR-ing
    // it would unmount the flow before the Complete screen shows.
    setStepsCompleted((prev) => {
      const featureHighlights =
        prev.featureHighlights || featureHighlightsComplete;
      // Nothing to update (e.g. a mid-flow account-setup server flip we
      // intentionally ignore) — return prev to skip an unnecessary rerender.
      if (featureHighlights === prev.featureHighlights) return prev;
      return { ...prev, featureHighlights };
    });
  }, [
    onboardingIsLoading,
    featureHighlightsComplete,
    accountSetupComplete,
    isInitialized,
  ]);
  const steps = useMemo(
    () => [
      {
        isComplete: stepsCompleted.featureHighlights,
        component: (
          <OnboardingFeatureHighlights
            onFeatureHighlightsComplete={async (highlightsError) => {
              if (highlightsError) {
                // Error occurred during feature highlights flow
                return;
              }

              try {
                await markFeatureHighlightsComplete();
              } catch (err) {
                // Error surfaced via useOnboarding().error
                return;
              }

              setStepsCompleted((prev) => ({
                ...prev,
                featureHighlights: true,
              }));
            }}
          />
        ),
      },
      {
        isComplete: stepsCompleted.accountSetup,
        component: (
          <OnboardingAccountSetup
            onOnboardingComplete={() => {
              setStepsCompleted((prev) => ({
                ...prev,
                accountSetup: true,
              }));
            }}
          />
        ),
      },
    ],
    [markFeatureHighlightsComplete, stepsCompleted],
  );

  const currentStep = useMemo(
    () => steps.find((step) => !step.isComplete),
    [steps],
  );

  // If all steps are complete, set isOnboardingComplete to true
  const isOnboardingComplete = !currentStep;

  return {
    currentStep,
    isOnboardingComplete,
    isLoading: onboardingIsLoading || !isInitialized,
    error,
  };
};
